<template>
  <div class="container clients">
    <div class="clients__title-container">
      <div class="clients__title-left">
        <div class="clients__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="clients__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="clients__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="clients__title-right"
           v-if="isDataFetched">
        <div class="clients__clientss">
          <span class="clients__count">Projects: {{totalItems}}</span>
          <base-select
            class="clients__grid-select"
            alignLeft
            showTopBorder
            shiftLabelRight
            :options="paginationOptions"
            :titleStyle="{'padding-left': '5px'}"
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button v-if="isUserSuperAdmin" @click.prevent="getCongestionReport" class="clients__button">
          Rank Report Congestion Report
        </button>
        <button v-if="isUserSuperAdmin" @click.prevent="openSetupWizard()" class="clients__button">
          Project Setup Wizard
        </button>
        <button @click.prevent="openEditPage('add-new-client')" class="clients__button">
          Create New Project
        </button>
      </div>
    </div>

    <div class="common-table clients__table">
      <div class="common-table-container">
        <div class="common-table__row common-table__header"
            v-if="isDataFetched">
          <div class="common-table__header-cell"
              v-for="column in clientsColumnsConfig"
              :key="column.property"
              :class="[`clients__${column.property}`]">
            <base-multi-select-sortable-header
              v-if="column.property !== 'edit-button'"
              class="clients__select-header"
              :column="column"
              :sort-config="sortConfig"
              :predefinedOptions="filterParams[column.property]"
              :predefinedNumberFilter="['percent_completed'].includes(column.property) ? filterParams[column.property] : null"
              :predefinedDateRangeFilter="['trial_end_date', 'last_ran', 'last_agg', 'next_scheduled_rank_report'].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
              @sort="onHeaderCellClicked(column, $event)"
              @apply="filterColumn($event, column)"
              @cancel="cancelFilter($event, column)"
              :ref="sortableHeaderRefs[column.property]"
            />
          </div>
        </div>
        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            No records found
          </div>
        </template>
        <div class="" v-else-if="!isDataFetched">
          <div class="clients__loader">
            <base-loader/>
          </div>
        </div>
        <template v-else>
          <template v-for="client in paginatedClients">
            <div class="common-table__row" @click="openEditPage(client.id)">
              <div class="common-table__cell"
                  v-for="column in clientsColumnsConfig"
                  :key="column.property"
                  :class="[`clients__${column.property}`, `clients__row_${column.property}`]">
                <template v-if="column.property === 'is_active'">
                  <span @click.stop="() => {}">
                    <base-select class="clients__select"
                                :options="activeOptions"
                                borderLess
                                alignLeft
                                shiftLabelRight
                                :selectStyle="{height: '60px!important', width: '75px', marginLeft: '-10px'}"
                                v-model="client.is_active"
                                @change="updateClientStatus(client)"/>
                  </span>
                </template>
                <template v-else-if="column.property === 'agency'">
                  <span>
                    {{ getAgency(client.agency) }}
                  </span>
                </template>
                <template v-else-if="['keyword_count'].includes(column.property)">
                  <span>
                    {{ client[column.property]?.toLocaleString() }}
                  </span>
                </template>
                <template v-else>
                    <span>
                      {{ client[column.property] }}
                    </span>
                </template>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="clients__update-loader" v-if="showUpdateLoader">
      <base-loader class="clients__update-loader-ring"/>
    </div>
  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'
import moment from 'moment'

const ITEMS_PER_PAGE = 100
const ACTIVE_OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
const EMPTY_FILTERS = {
  name: '',
  agency: [],
  is_active: [],
  last_ran: {
    date_from: null,
    date_to: null
  },
  last_agg: {
    date_from: null,
    date_to: null
  },
  next_scheduled_rank_report: {
    date_from: null,
    date_to: null
  },
  trial_end_date: {
    date_from: null,
    date_to: null
  },
  percent_completed: {
    operator: null,
    value: null
  },
  page_from: 0,
  page_to: ITEMS_PER_PAGE,
  keyword_count: null
}
// table config
const DEFAULT_CONFIG = [
  {
    label: 'Name',
    property: 'name'
  },
  {
    label: 'Organization',
    property: 'agency',
    isSmartInput: false,
    options: []
  },
  {
    label: 'Is Active',
    property: 'is_active',
    isSmartInput: false,
    options: [
      {
        label: 'Yes',
        value: 1
      },
      {
        label: 'No',
        value: -1
      }
    ]
  },
  {
    label: 'Project End Date',
    property: 'trial_end_date'
  },
  {
    label: 'Latest Report',
    property: 'last_ran'
  },
  {
    label: 'Percent Completed',
    property: 'percent_completed',
    isNumber: true,
    isClearOnCross: true,
    isSmartInput: false
  },
  {
    label: 'Last Agg',
    property: 'last_agg'
  },
  {
    label: 'Next Scheduled Report',
    property: 'next_scheduled_rank_report'
  },
  {
    label: 'Keywords',
    property: 'keyword_count',
    isNumber: true,
    isClearOnCross: true,
    isSmartInput: false
  },
  {
    label: 'Source',
    property: 'default_data_source',
  },
]
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
export default {
  name: 'Client',
  mixins: [BaseSortMixins],
  props: ['agencyFilterId'],
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      sortConfig: [{
        sortDirection: 'desc',
        propertyName: 'last_ran'
      }],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      clientsColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'agency': 'agency',
        'is_active': 'is_active',
        'trial_end_date': 'trial_end_date',
        'last_ran': 'last_ran',
        'percent_completed': 'percent_completed',
        'last_agg': 'last_agg',
        'next_scheduled_rank_report': 'next_scheduled_rank_report',
        'keyword_count': 'keyword_count'
      },
      activeOptions: [...ACTIVE_OPTIONS],
      paginationOptions: [...PAGINATION_OPTIONS]
    }
  },
  watch: {
    agencies: {
      handler (v) {
        this.clientsColumnsConfig.find(i => i.property === 'agency').options = v
      },
      deep: true
    },
    clientId: {
      handler (v) {
        if (v) {
          this.loadData()
          if (this.agencyFilterId && this.agencyFilterId.length) {
            try {
              !this.filterParams.agency.includes(this.agencyFilterId) && this.filterParams.agency.push(parseInt(this.agencyFilterId))
            } catch (e) {
            }
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('client', {
      paginatedClients (state) {
        let clients = _.cloneDeep(state.clients)
        // eslint-disable-next-line camelcase
        const { page_from = 0, page_to = 100, last_ran, last_agg, next_scheduled_rank_report, name, is_active, agency, percent_completed, trial_end_date } = this.filterParams
        let dateRangeFilter
        let projEndDateFilter
        let lastAggFilter
        // eslint-disable-next-line camelcase
        if (last_ran && last_ran.date_from && last_ran.date_to) {
          const dateFrom = moment(last_ran.date_from)
          const dateTo = moment(last_ran.date_to)
          dateRangeFilter = (date) => {
            date = moment(date)
            return dateFrom <= date && date <= dateTo
          }
        }
        // eslint-disable-next-line camelcase
        if (last_agg && last_agg.date_from && last_agg.date_to) {
          const dateFrom = moment(last_agg.date_from)
          const dateTo = moment(last_agg.date_to)
          lastAggFilter = (date) => {
            date = moment(date)
            return dateFrom <= date && date <= dateTo
          }
        }
        // eslint-disable-next-line camelcase
        if (trial_end_date && trial_end_date.date_from && trial_end_date.date_to) {
          const dateFrom = moment(trial_end_date.date_from)
          const dateTo = moment(trial_end_date.date_to)
          projEndDateFilter = (date) => {
            date = moment(date)
            return dateFrom <= date && date <= dateTo
          }
        }
        let nextScheduledRankReportFilter
        // eslint-disable-next-line camelcase
        if (next_scheduled_rank_report && next_scheduled_rank_report.date_from && next_scheduled_rank_report.date_to) {
          const dateFrom = moment(next_scheduled_rank_report.date_from)
          const dateTo = moment(next_scheduled_rank_report.date_to)
          nextScheduledRankReportFilter = (date) => {
            date = moment(date)
            return dateFrom <= date && date <= dateTo
          }
        }
        // eslint-disable-next-line camelcase
        if (is_active && is_active.includes(-1)) is_active.push(0)
        clients = clients.filter(item => {
          const nameFilter = !name || item.name.toLowerCase().includes(name.toLowerCase())
          // eslint-disable-next-line camelcase
          const isActiveFilter = (!is_active || !is_active.length) || is_active.includes(item.is_active)
          const agencyFilter = (!agency || !agency.length) || agency.includes(item.agency)
          const dateFilter = !dateRangeFilter || dateRangeFilter(item.last_ran)
          const projDateFilter = !projEndDateFilter || projEndDateFilter(item.trial_end_date)
          const aggFilter = !lastAggFilter || lastAggFilter(item.last_agg)
          const scheduledRankReportFilter = !nextScheduledRankReportFilter || nextScheduledRankReportFilter(item.next_scheduled_rank_report)
          const reportCompletion = (
            (!percent_completed.operator) ||
            (percent_completed.operator === 'equal' && +percent_completed.value === item.percent_completed) ||
            (percent_completed.operator === 'ne' && +percent_completed.value !== item.percent_completed) ||
            (percent_completed.operator === 'gt' && +percent_completed.value < item.percent_completed) ||
            (percent_completed.operator === 'lt' && +percent_completed.value > item.percent_completed) ||
            (percent_completed.operator === 'lte' && +percent_completed.value >= item.percent_completed) ||
            (percent_completed.operator === 'gte' && +percent_completed.value <= item.percent_completed)
          )
          return nameFilter && isActiveFilter && agencyFilter && dateFilter && reportCompletion && aggFilter && scheduledRankReportFilter && projDateFilter
        })
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.totalItems = clients.length
        return helper.sortByMultipleParams(clients, this.sortConfig).slice(page_from, page_to)
      },
      agencies: 'agencies',
      isSettingsQueried: 'isSettingsQueried'
    }),
    dataMissing () {
      return this.isDataFetched && !this.paginatedClients.length
    },
    hasPagination () {
      return this.isDataFetched && !this.isDataUpdating && this.totalItems > this.pageItemsLimit
    },
    showUpdateLoader () {
      return this.isDataFetched && this.isDataUpdating
    },
    isUserSuperAdmin () {
      return this.userRoles.includes('admin')
    }
  },
  methods: {
    ...mapActions('client', ['getClientsData', 'changeClientStatus', 'getClientsSettings']),
    async loadData () {
      this.isDataUpdating = true
      const queries = [this.getClientsData()]
      !this.isSettingsQueried && queries.push(this.getClientsSettings(this.clientId))
      await Promise.all(queries)
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    openSetupWizard () {
      this.$router.push({ name: 'setup-wizard' })
    },
    openEditPage (id) {
      if (id === 'add-new-client') {
        this.$router.push({ path: `clients/${id}` })
        return
      }
      this.$router.push({
        name: 'project-settings',
        params: { projectId: id }
      })
    },
    getCongestionReport () {
      window.location.href = `/rest/clients/congestion_report?_cache=${Math.floor(Math.random() * 9999999) + 1}`
    },
    getAgency (agencyId) {
      const agency = this.agencies.find(i => i.value === agencyId) || {}
      return agency.label || 'Not Set'
    },
    reset () {
      this.sortConfig = []
      this.totalItems = 0
      this.clientsColumnsConfig = _.cloneDeep(DEFAULT_CONFIG)
      this.loadPageData()
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async updateClientStatus (client) {
      this.isDataUpdating = true
      await this.changeClientStatus(client)
      this.isDataUpdating = false
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      if (['created', 'updated'].includes(column.property)) {
        this.filterParams[column.property].date_from = event.date_from
        this.filterParams[column.property].date_to = event.date_to
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = EMPTY_FILTERS[column.property]
    }
  }
}
</script>

<style scoped lang="scss">

.common-table-container {
  overflow-x: scroll;
  display: flex;
  flex-direction: column;
}
.common-table {
  border-radius: 4px;
  overflow: hidden;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:first-child {
      padding-left: 25px;
    }
    &:last-child {
      padding-right: 25px;
    }
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    padding-left: 25px;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }
    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    min-width: 1900px;
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;

    &:not(.common-table__header) {
      &:hover {
        cursor: pointer;
        background-color: rgba(40, 74, 255, .1) !important;
      }
    }
  }
}

.clients {
  position: relative;
  min-height: 200vh !important;

  &__keyword-header {
    background-color: rgba(1, 22, 39, 0.1);
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .clients__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    // position: relative;
    // display: flex;
    // flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__clientss {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

    &__edit-button {
      flex: 0 0 45px;

      &.common-table__header-cell {
        flex-basis: 0;
      }
    }

  &__row {
    // &_name {
    //   flex-basis: 1.2%;
    // }

    // &_category {
    //   flex-basis: 0.9%;
    // }

    // &_created {
    //   flex-basis: 1%;
    // }
  }

  &__name {

    &.common-table__header-cell {
      // flex-basis: 2%;

      // &::v-deep .multi-select__title-container {
      //   padding-left: 25px;
      // }
    }
  }

  &__name,
  &__keyword_count,
  &__agency,
  &__percent_completed,
  &__last_agg {
    flex: 1 0 140px;
    &.common-table__header-cell {
      flex: 1 0 140px;
    }
  }

  &__last_ran, &__trial_end_date {
    flex: 1 0 160px;
    &.common-table__header-cell {
      flex: 1 0 160px;
    }
  }

  &__next_scheduled_rank_report {
    flex: 1 0 210px;
    &.common-table__header-cell {
      flex: 1 0 210px;
    }
  }

  &__is_active {
    flex: 1 0 80px;
    &.common-table__header-cell {
      flex: 1 0 80px;
    }
  }
  &__default_data_source {
    flex: 1 0 200px;
    &.common-table__header-cell {
      flex: 1 0 200px;
    }
  }
  // &__last_ran {

  //   &.common-table__header-cell {
  //     flex-basis: 1%;
  //   }
  // }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: 4px;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}
</style>
